// Products table — Airtable/Linear inspired dense grid with inline editing.
// Each row tracks its own "dirty" state and exposes a Save button that pushes
// to /api/products/{id}.
import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import {
  ExternalLink, ImageOff, Loader2, Save, Undo2, Search, ChevronDown,
} from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "@/components/ui/popover";
import { fmtRelative } from "@/lib/format";
import { updateProduct } from "@/lib/products";
import { updateItem as updateInventoryItem } from "@/lib/inventory";

const STATUS_BADGES = {
  ACTIVE: { label: "Activ", className: "bg-emerald-500/10 text-emerald-500 border-emerald-500/30" },
  DRAFT: { label: "Draft", className: "bg-zinc-500/10 text-zinc-400 border-zinc-500/30" },
  ARCHIVED: { label: "Arhivat", className: "bg-orange-500/10 text-orange-500 border-orange-500/30" },
};

const COLS = [
  { id: "checkbox", w: 36, sticky: true, label: "" },
  { id: "image", w: 56, sticky: true, label: "" },
  { id: "title", w: 320, label: "Produs" },
  { id: "sku", w: 150, label: "SKU" },
  { id: "cost", w: 110, label: "Cost", align: "right" },
  { id: "compare", w: 110, label: "Preț întreg", align: "right" },
  { id: "price", w: 110, label: "Preț redus", align: "right" },
  { id: "stock", w: 90, label: "Stoc", align: "right" },
  { id: "supplier", w: 130, label: "Furnizor" },
  { id: "min_stock", w: 80, label: "Prag min", align: "right" },
  { id: "status", w: 96, label: "Status" },
  { id: "updated", w: 130, label: "Actualizat" },
  { id: "save", w: 110, label: "" },
  { id: "actions", w: 60, label: "" },
];

export default function ProductsTable({
  items, loading, loadingMore, selected, onToggleSelect, onSelectAll,
  onOpen, activeProductId, onReplaceItem, suppliers = [], onDirtyChange,
}) {
  const allSelected = items.length > 0 && items.every((i) => selected.has(i.id));
  const someSelected = !allSelected && items.some((i) => selected.has(i.id));

  return (
    <table className="w-full text-sm border-collapse" data-testid="products-table">
      <colgroup>
        {COLS.map((c) => <col key={c.id} style={{ width: c.w }} />)}
      </colgroup>
      <thead className="sticky top-0 z-10 bg-secondary/40 backdrop-blur">
        <tr className="border-b border-border">
          <th className="px-2 py-2 text-left">
            <Checkbox
              checked={allSelected ? true : (someSelected ? "indeterminate" : false)}
              onCheckedChange={onSelectAll}
              data-testid="products-select-all"
            />
          </th>
          {COLS.slice(1).map((c) => (
            <th
              key={c.id}
              className={`px-3 py-2 text-[10px] uppercase tracking-widest text-muted-foreground font-semibold text-${c.align || "left"}`}
            >
              {c.label}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {loading && items.length === 0 ? (
          Array.from({ length: 12 }).map((_, i) => (
            <tr key={i} className="border-b border-border/50">
              {COLS.map((c) => (
                <td key={c.id} className="px-3 py-2.5">
                  <div className="h-5 bg-secondary/40 animate-pulse rounded" />
                </td>
              ))}
            </tr>
          ))
        ) : items.length === 0 ? (
          <tr>
            <td colSpan={COLS.length} className="text-center py-16 text-muted-foreground">
              Niciun produs găsit. Folosește butonul „Sync” pentru a importa din Shopify.
            </td>
          </tr>
        ) : (
          items.map((p) => (
            <ProductRow
              key={p.id}
              product={p}
              selected={selected.has(p.id)}
              active={activeProductId === p.id}
              onToggleSelect={() => onToggleSelect(p.id)}
              onOpen={() => onOpen(p.id)}
              onReplaceItem={onReplaceItem}
              suppliers={suppliers}
              onDirtyChange={onDirtyChange}
            />
          ))
        )}
        {loadingMore && (
          <tr><td colSpan={COLS.length} className="py-4 text-center">
            <Loader2 className="h-4 w-4 animate-spin inline" />
          </td></tr>
        )}
      </tbody>
    </table>
  );
}


function ProductRow({ product, selected, active, onToggleSelect, onOpen, onReplaceItem, suppliers = [], onDirtyChange }) {
  // Track only the FIRST variant inline (most Shopify products are single-variant).
  // Multi-variant products edit via the sidebar.
  const v0 = (product.variants || [])[0] || {};
  const inv0 = product.inventory || {};
  const original = useMemo(() => ({
    sku: v0.sku || "",
    cost: v0.cost ?? "",
    compare_at_price: v0.compare_at_price ?? "",
    price: v0.price ?? "",
    inventory_quantity: v0.inventory_quantity ?? 0,
    supplier_id: inv0.supplier_id || "",
    min_stock: inv0.min_stock ?? 0,
  }), [v0.sku, v0.cost, v0.compare_at_price, v0.price, v0.inventory_quantity, inv0.supplier_id, inv0.min_stock]);

  const [draft, setDraft] = useState(original);
  const [saving, setSaving] = useState(false);

  // Reset when the row's underlying data changes (after server refresh)
  useEffect(() => { setDraft(original); }, [original]);

  const dirty = (
    String(draft.sku) !== String(original.sku) ||
    String(draft.cost) !== String(original.cost) ||
    String(draft.compare_at_price) !== String(original.compare_at_price) ||
    String(draft.price) !== String(original.price) ||
    String(draft.inventory_quantity) !== String(original.inventory_quantity) ||
    String(draft.supplier_id) !== String(original.supplier_id) ||
    String(draft.min_stock) !== String(original.min_stock)
  );

  // Bubble dirty state up so the parent can show a global pending bar.
  // We pass a stable wrapper that always calls the latest `save` via a ref,
  // so the parent never holds a stale closure.
  const saveRef = useRef(null);
  useEffect(() => {
    if (dirty) {
      onDirtyChange?.(product.id, true, () => saveRef.current?.());
    } else {
      onDirtyChange?.(product.id, false);
    }
    return () => onDirtyChange?.(product.id, false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dirty]);

  const status = (product.status || "ACTIVE").toUpperCase();
  const sb = STATUS_BADGES[status] || STATUS_BADGES.ACTIVE;
  const isMulti = (product.variants_count || 0) > 1;
  const editable = !isMulti;

  const setField = (k, val) => {
    setDraft((d) => ({ ...d, [k]: val }));
  };

  const discount = useMemo(() => {
    const cap = parseFloat(draft.compare_at_price);
    const p = parseFloat(draft.price);
    if (!cap || !p || cap <= p) return null;
    return Math.round(((cap - p) / cap) * 100);
  }, [draft.compare_at_price, draft.price]);

  const margin = useMemo(() => {
    const c = parseFloat(draft.cost);
    const p = parseFloat(draft.price);
    if (!c || !p || p <= 0) return null;
    return Math.round(((p - c) / p) * 100);
  }, [draft.cost, draft.price]);

  const save = async () => {
    if (!dirty || saving) return;
    setSaving(true);
    const tid = toast.loading("Salvez...");
    try {
      // 1) variant fields (Shopify) — only call if a variant field changed
      const variantDirty = (
        String(draft.sku) !== String(original.sku) ||
        String(draft.cost) !== String(original.cost) ||
        String(draft.compare_at_price) !== String(original.compare_at_price) ||
        String(draft.price) !== String(original.price) ||
        String(draft.inventory_quantity) !== String(original.inventory_quantity)
      );
      let messages = [];
      if (variantDirty) {
        const body = {
          variants: [{
            variant_id: v0.variant_id,
            sku: draft.sku,
            cost: draft.cost === "" ? null : String(draft.cost),
            compare_at_price: draft.compare_at_price === "" ? null : String(draft.compare_at_price),
            price: String(draft.price),
            inventory_quantity: Number(draft.inventory_quantity),
          }],
        };
        const r = await updateProduct(product.id, body);
        if (!r.ok && r.errors?.length) {
          throw new Error(r.errors[0]);
        }
        messages.push(`${r.changes_applied || 0} în Shopify`);
        if (r.product) onReplaceItem?.(product.id, r.product);
      }
      // 2) inventory fields (supplier_id, min_stock) — separate endpoint
      const invDirty = (
        String(draft.supplier_id) !== String(original.supplier_id) ||
        String(draft.min_stock) !== String(original.min_stock)
      );
      if (invDirty) {
        if (!v0.variant_id) {
          throw new Error("Variant fără inventory_item — rulează Init în Stocuri.");
        }
        const patch = {};
        if (String(draft.supplier_id) !== String(original.supplier_id)) {
          patch.supplier_id = draft.supplier_id || null;
        }
        if (String(draft.min_stock) !== String(original.min_stock)) {
          patch.min_stock = parseInt(draft.min_stock, 10) || 0;
        }
        // inventory_item.id == variant_id (deterministic — see engine.init_from_shopify)
        const out = await updateInventoryItem(v0.variant_id, patch);
        const supplier_name = out.supplier_name;
        // Optimistically reflect the new inventory in the row's product
        onReplaceItem?.(product.id, {
          inventory: {
            ...inv0,
            supplier_id: out.supplier_id,
            supplier_name,
            min_stock: out.min_stock,
          },
        });
        messages.push("furnizor/prag");
      }
      toast.success(messages.length ? `Salvat: ${messages.join(" + ")}` : "Salvat", { id: tid });
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "Eroare la salvare", { id: tid });
    } finally {
      setSaving(false);
    }
  };

  const reset = () => setDraft(original);
  saveRef.current = save;

  return (
    <tr
      className={`border-b border-border/50 transition-colors group ${
        active ? "bg-primary/5" : dirty ? "bg-amber-500/[0.04]" : "hover:bg-secondary/30"
      }`}
      data-testid={`product-row-${product.shopify_id}`}
    >
      {/* Checkbox */}
      <td className="px-2 py-2">
        <Checkbox
          checked={selected}
          onCheckedChange={onToggleSelect}
          data-testid={`product-select-${product.shopify_id}`}
        />
      </td>

      {/* Image */}
      <td className="px-2 py-2">
        <button
          onClick={onOpen}
          className="h-10 w-10 rounded-md bg-secondary overflow-hidden flex items-center justify-center hover:ring-2 hover:ring-primary/50 transition"
          data-testid={`product-open-${product.shopify_id}`}
        >
          {product.featured_image ? (
            <img
              src={product.featured_image}
              alt={product.title}
              loading="lazy"
              className="h-full w-full object-cover"
            />
          ) : (
            <ImageOff className="h-4 w-4 text-muted-foreground" />
          )}
        </button>
      </td>

      {/* Title + vendor */}
      <td className="px-3 py-2 min-w-0">
        <button
          onClick={onOpen}
          className="text-left w-full min-w-0 group"
        >
          <div className="text-sm font-medium truncate hover:text-primary transition">
            {product.title}
          </div>
          <div className="text-[11px] text-muted-foreground truncate flex items-center gap-1.5">
            {product.vendor && <span>{product.vendor}</span>}
            {isMulti && (
              <Badge variant="outline" className="text-[9px] h-4 px-1.5 py-0">
                {product.variants_count} variante
              </Badge>
            )}
          </div>
        </button>
      </td>

      {/* SKU */}
      <CellInput
        value={draft.sku}
        onChange={(v) => setField("sku", v)}
        disabled={!editable}
        mono
        testid={`product-sku-${product.shopify_id}`}
      />

      {/* Cost */}
      <CellInput
        value={draft.cost}
        onChange={(v) => setField("cost", v)}
        type="number"
        align="right"
        disabled={!editable}
        suffix={margin != null ? `${margin}%` : null}
        suffixTitle={margin != null ? `Marjă ~${margin}%` : null}
        testid={`product-cost-${product.shopify_id}`}
      />

      {/* compare_at_price */}
      <CellInput
        value={draft.compare_at_price}
        onChange={(v) => setField("compare_at_price", v)}
        type="number"
        align="right"
        disabled={!editable}
        testid={`product-compare-${product.shopify_id}`}
      />

      {/* price */}
      <CellInput
        value={draft.price}
        onChange={(v) => setField("price", v)}
        type="number"
        align="right"
        disabled={!editable}
        suffix={discount != null ? `-${discount}%` : null}
        suffixTitle={discount != null ? `Discount ${discount}%` : null}
        suffixColor="text-red-500"
        testid={`product-price-${product.shopify_id}`}
      />

      {/* stock */}
      <CellInput
        value={draft.inventory_quantity}
        onChange={(v) => setField("inventory_quantity", v)}
        type="number"
        align="right"
        disabled={!editable}
        testid={`product-stock-${product.shopify_id}`}
      />

      {/* supplier (inline editable) */}
      <td className="px-1.5 py-1" data-testid={`product-supplier-${product.shopify_id}`}>
        <SupplierPicker
          value={draft.supplier_id}
          currentName={
            draft.supplier_id === original.supplier_id
              ? (product.inventory?.supplier_name || "")
              : (suppliers.find((s) => s.id === draft.supplier_id)?.name || "")
          }
          suppliers={suppliers}
          onChange={(sid) => setField("supplier_id", sid)}
          testid={`product-supplier-picker-${product.shopify_id}`}
        />
      </td>

      {/* min stock (inline editable) */}
      <td className="px-1.5 py-1 text-right" data-testid={`product-minstock-${product.shopify_id}`}>
        <input
          type="number"
          min="0"
          step="1"
          value={String(draft.min_stock)}
          onChange={(e) => {
            const v = e.target.value;
            // strict non-negative integer; reject anything else and strip leading zeros
            if (v === "") return setField("min_stock", 0);
            if (!/^\d+$/.test(v)) return; // reject letters / minus / dots
            setField("min_stock", parseInt(v, 10));
          }}
          className="w-full h-8 px-2 rounded-md bg-transparent hover:bg-secondary/50 focus:bg-background focus:ring-1 focus:ring-primary outline-none text-sm tabular-nums text-right font-mono"
          data-testid={`product-minstock-input-${product.shopify_id}`}
        />
      </td>

      {/* status */}
      <td className="px-3 py-2">
        <Badge variant="outline" className={`text-[10px] ${sb.className}`}>
          {sb.label}
        </Badge>
      </td>

      {/* updated */}
      <td className="px-3 py-2 text-[11px] text-muted-foreground tabular-nums whitespace-nowrap">
        {product.shopify_updated_at ? fmtRelative(product.shopify_updated_at) : "—"}
      </td>

      {/* save */}
      <td className="px-2 py-2">
        {dirty ? (
          <div className="flex items-center gap-1">
            <Button
              size="sm"
              className="h-7 px-2 bg-amber-500 hover:bg-amber-600 text-white"
              onClick={save}
              disabled={saving}
              data-testid={`product-save-${product.shopify_id}`}
            >
              {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3" />}
              <span className="ml-1 text-[11px]">Save</span>
            </Button>
            <button
              onClick={reset}
              className="text-muted-foreground hover:text-foreground"
              title="Resetează modificările"
              data-testid={`product-reset-${product.shopify_id}`}
            >
              <Undo2 className="h-3.5 w-3.5" />
            </button>
          </div>
        ) : (
          <span className="text-[10px] text-muted-foreground/60">—</span>
        )}
      </td>

      {/* actions */}
      <td className="px-2 py-2">
        <a
          href={`https://ieftinit-2841.myshopify.com/admin/products/${product.shopify_id}`}
          target="_blank"
          rel="noreferrer"
          className="text-muted-foreground hover:text-foreground inline-flex items-center"
          title="Deschide în Shopify Admin"
          data-testid={`product-shopify-${product.shopify_id}`}
        >
          <ExternalLink className="h-3.5 w-3.5" />
        </a>
      </td>
    </tr>
  );
}


function CellInput({
  value, onChange, type = "text", align = "left", mono = false,
  disabled = false, suffix = null, suffixTitle = null, suffixColor = "text-emerald-500", testid,
}) {
  if (disabled) {
    return (
      <td className={`px-3 py-2 text-${align}`}>
        <span className={`text-xs text-muted-foreground ${mono ? "font-mono" : ""}`}>
          {value === "" || value == null ? "—" : value}
        </span>
      </td>
    );
  }
  return (
    <td className={`px-1.5 py-1 text-${align}`}>
      <div className="relative">
        <input
          value={value ?? ""}
          onChange={(e) => onChange(e.target.value)}
          type={type}
          step="0.01"
          inputMode={type === "number" ? "decimal" : undefined}
          className={`w-full h-8 px-2 ${suffix ? "pr-10" : ""} rounded-md bg-transparent hover:bg-secondary/50 focus:bg-background focus:ring-1 focus:ring-primary outline-none text-sm tabular-nums ${
            mono ? "font-mono" : ""
          } text-${align}`}
          data-testid={testid}
        />
        {suffix && (
          <span
            className={`absolute right-2 top-1/2 -translate-y-1/2 text-[10px] font-semibold pointer-events-none ${suffixColor}`}
            title={suffixTitle || undefined}
          >
            {suffix}
          </span>
        )}
      </div>
    </td>
  );
}



function SupplierPicker({ value, currentName, suppliers, onChange, testid }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const selected = suppliers.find((s) => s.id === value);
  const display = selected?.name || currentName || "";
  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return suppliers;
    return suppliers.filter((s) => (s.name || "").toLowerCase().includes(needle));
  }, [q, suppliers]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="w-full h-8 px-2 rounded-md bg-transparent hover:bg-secondary/50 focus:bg-background focus:ring-1 focus:ring-primary outline-none text-xs text-left flex items-center gap-1.5"
          data-testid={testid}
        >
          <span className={`truncate flex-1 ${display ? "" : "text-muted-foreground/60"}`}>
            {display || "Fără furnizor"}
          </span>
          <ChevronDown className="h-3 w-3 text-muted-foreground shrink-0" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="p-0 w-64">
        <div className="p-2 border-b border-border">
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
            <Input
              autoFocus
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Caută furnizor..."
              className="h-8 pl-8 text-xs"
              data-testid={`${testid}-search`}
            />
          </div>
        </div>
        <div className="max-h-60 overflow-auto py-1">
          <button
            type="button"
            onClick={() => { onChange(""); setOpen(false); }}
            className={`w-full text-left px-3 py-1.5 text-xs hover:bg-secondary ${!value ? "text-primary font-medium" : "text-muted-foreground"}`}
            data-testid={`${testid}-none`}
          >
            Fără furnizor
          </button>
          {filtered.length === 0 ? (
            <div className="px-3 py-3 text-xs text-muted-foreground text-center">
              {suppliers.length === 0 ? "Niciun furnizor (adaugă din Stocuri → Furnizori)" : "Niciun rezultat"}
            </div>
          ) : (
            filtered.map((s) => (
              <button
                key={s.id}
                type="button"
                onClick={() => { onChange(s.id); setOpen(false); }}
                className={`w-full text-left px-3 py-1.5 text-xs hover:bg-secondary truncate ${value === s.id ? "text-primary font-medium bg-primary/5" : ""}`}
                data-testid={`${testid}-opt-${s.id}`}
              >
                {s.name}
              </button>
            ))
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
